// Konfiguration's data model — "hvordan er det sat op, og hvad anbefaler vi?" One entry per area:
//   settings: rated rows (a setting we can read and have an opinion on: I orden / bør ses på / bør rettes)
//   groups:   plain lists of the objects behind the area (policies, apps, roles, sites, users ...)
// A row is { id, kind, title, sub, value, tone, meaning, situation, recommend, evidence, item }.
// Only `settings` count in the overview bars; groups are reference, not verdicts.
// Same rule as everywhere: a field we cannot read shows CANNOT_FETCH, never a guessed value.
import { buildSettingsFor } from './settingsRegistry';
import { buildExchangeSettings } from './exchangeSettings';
import { SETTINGS_AREA_ORDER } from './settingsAreas';
import { displayCaPolicyName } from '../utils/caPolicyName';
import { CANNOT_FETCH, shortDate, scopeLine } from '../utils/policyDisplay';
import { noReportedStatusCount } from '../hooks/useFindingGroups';

// Licenser is not a Konfiguration area (it is a subscription, not a setup).
export const CONFIG_AREAS = SETTINGS_AREA_ORDER.filter((a) => a !== 'licenses');

// Which of Intune/Exchange's own categories belong to each policy-based area.
const AREA_CATEGORIES = {
  devices: ['compliance', 'configuration', 'settingsCatalog', 'endpointSecurity', 'adminTemplate', 'update', 'autopilot', 'enrollment'],
  exchange: ['mail'],
  applications: ['appProtection', 'appConfig'],
};

const TONE = { good: 'ok', warn: 'warn', bad: 'bad' };
const CA_STATE = { enabled: 'Aktiveret', disabled: 'Deaktiveret', enabledForReportingButNotEnforced: 'Kun rapportering' };

const byName = (a, b) => (a.title || '').localeCompare(b.title || '', 'da');

function settingRow(s) {
  return {
    id: `set:${s.key}`,
    kind: 'setting',
    title: s.title,
    sub: s.description,
    value: s.value === null || s.value === undefined ? CANNOT_FETCH : String(s.value),
    tone: TONE[s.tone] || 'neutral',
    meaning: s.meaning,
    situation: s.situation,
    record: s.record,
    recommend: s.recommend,
    evidence: s.evidence,
  };
}

function caRow(p) {
  const name = displayCaPolicyName(p.displayName);
  const requires = (p.controlsShort || []).join(', ') || 'intet konfigureret';
  const scope = p.targetSummaryShort || 'alle brugere';
  const off = p.state === 'disabled';
  const report = p.state === 'enabledForReportingButNotEnforced';
  return {
    id: `ca:${p.id}`,
    kind: 'setting',
    title: name,
    sub: `Kræver ${requires} · Gælder for ${scope}`,
    value: CA_STATE[p.state] || p.state || CANNOT_FETCH,
    tone: off || report ? 'warn' : 'ok',
    meaning: 'En loginregel (Conditional Access) bestemmer, hvornår og hvordan en bruger skal godkende sig, før der gives adgang.',
    situation: `Kræver ${requires}. Gælder for ${scope}.${off ? ' Reglen er slået fra.' : report ? ' Reglen logger kun og håndhæver ikke.' : ''}`,
    recommend: off
      ? 'Reglen er slået fra og beskytter ikke lige nu. Slå den til, eller fjern den, hvis den ikke skal bruges.'
      : report ? 'Reglen håndhæver ikke noget endnu. Aktivér den, når I har set effekten i rapporten.' : null,
    evidence: [['Observed value', p.state || CANNOT_FETCH], ['Source', 'Microsoft Entra / Graph']],
    // The whole rule as the backend read it: who it covers (and who is left out), which apps, where, and what it requires.
    ca: p,
  };
}

function policyRows(category, inv, scope) {
  return [...category.items].map((item) => {
    const line = scopeLine(item, scope, inv.groups);
    return {
      id: `pol:${item.id}`,
      kind: 'policy',
      title: item.name,
      sub: [category.label, item.updateKind, shortDate(item.lastModifiedDateTime)].filter(Boolean).join(' · '),
      value: line.deviation ? `${line.text} · ${line.deviation}` : line.text,
      tone: line.tone === 'warn' ? 'warn' : 'neutral',
      item,
    };
  }).sort(byName);
}

function staticRow(id, title, value, sub, tone = 'neutral') {
  return { id, kind: 'object', title, sub, value, tone };
}

// ctx: everything the page has already loaded (nothing is fetched here).
export function buildConfig(ctx) {
  const { mfa, ca, applications, sharePointSettings, devices, resources, risks, insights, inv, scope, sites, workspaces, checkedAt } = ctx;
  const out = Object.fromEntries(CONFIG_AREAS.map((a) => [a, { settings: [], groups: [] }]));

  // Rated settings, straight from the registry the old Microsoft-indstillinger box used. devices'
  // own noThreatAgentCount field counts every unknown-status device, even ones already flagged for a
  // real compliance reason elsewhere — noReportedStatusCount is the same, narrower count Arbejdskø's
  // "Ingen rapporteret sikkerhedsstatus" finding uses, so the two pages can't disagree on it.
  const correctedNoAgent = noReportedStatusCount(devices?.devices);
  const devicesCtx = devices && correctedNoAgent !== null ? { ...devices, noThreatAgentCount: correctedNoAgent } : devices;
  const settingsCtx = { devices: devicesCtx, resources, sharePointSettings, applications, risks, ca, checkedAt };
  ['identity', 'devices', 'security', 'sharepoint', 'applications', 'roles'].forEach((area) => {
    out[area].settings = Object.values(buildSettingsFor(area, settingsCtx)).map(settingRow);
  });

  if (insights?.emailAuth) {
    out.exchange.settings = Object.values(buildExchangeSettings(insights, checkedAt)).map(settingRow);
  }

  out['conditional-access'].settings = [...(ca?.policies || [])].map(caRow).sort(byName);

  // Object lists.
  const invOk = inv && !inv.error;
  if (invOk) {
    Object.entries(AREA_CATEGORIES).forEach(([area, keys]) => {
      keys.map((k) => inv.categories[k]).filter(Boolean).forEach((c) => {
        out[area].groups.push({ key: `${area}:${c.label}`, label: c.label, unit: ['politik', 'politikker'], rows: policyRows(c, inv, scope) });
      });
    });
  }

  const apps = (applications?.applications || []).filter((a) => !a.isMicrosoftApp);
  if (apps.length) {
    out.applications.groups.push({
      key: 'applications:apps', label: 'Enterprise Apps', unit: ['app', 'apps'],
      rows: apps.map((a) => staticRow(`app:${a.id}`, a.displayName || 'Ukendt', `${a.permissionCount ?? 0} tilladelser`, a.publisherName || '')).sort(byName),
    });
  }

  // "Permanent" excludes 'activated' (a PIM role switched on temporarily right now) the same way
  // Arbejdskø's own "Permanent Global Administrator" finding does — otherwise someone who just
  // activated a role via PIM for an hour would count as having it on permanently in both places.
  const byRole = new Map();
  (risks?.privilegedRoles || []).forEach((r) => {
    const name = r.role || 'Ukendt rolle';
    if (!byRole.has(name)) byRole.set(name, { permanent: 0, eligible: 0 });
    const b = byRole.get(name);
    if (r.status === 'eligible') b.eligible++; else if (r.status !== 'activated') b.permanent++;
  });
  if (byRole.size) {
    out.roles.groups.push({
      key: 'roles:roles', label: 'Roller', unit: ['rolle', 'roller'],
      rows: [...byRole.entries()].map(([name, c]) => staticRow(`role:${name}`, name, `${c.permanent} permanent`, c.eligible > 0 ? `${c.eligible} PIM-berettiget` : 'Ingen PIM-berettigede')).sort(byName),
    });
  }

  if ((sites || []).length) {
    out.sharepoint.groups.push({
      key: 'sharepoint:sites', label: 'SharePoint-sites', unit: ['site', 'sites'],
      rows: sites.map((s) => staticRow(`site:${s.id}`, s.displayName || 'Ukendt', 'SharePoint-site', s.lastActivityDateTime ? `Sidst aktiv ${shortDate(s.lastActivityDateTime)}` : CANNOT_FETCH)).sort(byName),
    });
  }
  if ((workspaces || []).length) {
    out.sharepoint.groups.push({
      key: 'sharepoint:groups', label: 'Grupper & Teams', unit: ['gruppe', 'grupper'],
      // Plain facts, not a verdict on the group — a person deciding "too many guests here" is
      // Arbejdskø's job, not a red mark on an inventory row.
      rows: workspaces.map((w) => {
        const guests = w.guestCount || 0;
        const what = (w.resources || []).join(' + ') || 'Microsoft 365-gruppe';
        return staticRow(`grp:${w.id}`, w.displayName || 'Ukendt', `${w.memberCount ?? 0} medlemmer`, guests > 0 ? `${guests} gæster · ${what}` : what);
      }).sort(byName),
    });
  }

  const users = mfa?.users || [];
  if (users.length) {
    // Plain per-person fact, alphabetical — not coloured or sorted worst-first, which would just be
    // Arbejdskø's "who needs MFA" finding restated here. That case-by-case work stays in Arbejdskø;
    // the aggregate ("2 af 20 mangler MFA") above is the one number this page has an opinion on.
    out.identity.groups.push({
      key: 'identity:users', label: 'Brugere og MFA', unit: ['bruger', 'brugere'],
      rows: users.map((u) => staticRow(`usr:${u.id}`, u.displayName || u.userPrincipalName || 'Ukendt', u.hasMfa ? 'MFA aktiveret' : 'MFA mangler', u.userPrincipalName || '')).sort(byName),
    });
  }
  const guests = risks?.guests || [];
  if (guests.length) {
    out.identity.groups.push({
      key: 'identity:guests', label: 'Gæstebrugere', unit: ['gæst', 'gæster'],
      rows: guests.map((g) => staticRow(`gst:${g.id}`, g.displayName || 'Ukendt', 'Gæst', g.mail || g.userPrincipalName || CANNOT_FETCH)).sort(byName),
    });
  }

  return out;
}

// Overview numbers for a list of rated rows.
export function configStats(rows) {
  const s = { ok: 0, warn: 0, bad: 0, other: 0, total: rows.length };
  rows.forEach((r) => { if (r.tone === 'ok' || r.tone === 'warn' || r.tone === 'bad') s[r.tone]++; else s.other++; });
  const rated = s.ok + s.warn + s.bad;
  s.rated = rated;
  s.recommendations = s.warn + s.bad;
  return s;
}
